/**
 * `ask` narrowing (D231) — who is asked, and what about.
 *
 * @remarks
 * Extracted from `narrow-parsed-command.ts` at the 500-line cap, as the
 * remove and instrument arms were. Structure only, as everywhere in
 * narrowing: a question is never refused here — the handler decides what
 * answers it, down the ladder `ask-handler.ts` describes.
 *
 * @packageDocumentation
 */

import type { EntityRef, ParsedCommand } from '@orpheus/parser-engine';

import type { AskParty, AskSubject, CommandNarrowingOutcome } from './runtime-action.js';

import { actorIdForEntityRef, itemIdForEntityRef, roomIdForEntityRef, topicWordForEntityRef } from '../entities/entity-ref.js';
import { narrowed } from './narrow-parsed-command.js';

/**
 * Narrow `ask` (D231): the direct object is who is ASKED, the `about` phrase
 * is what is asked ABOUT — a thing, the room (D137, once its own action), or
 * a bare topic word the parser read from the topic catalog (D226's rule: a
 * question's subject may be absent; nothing else may). `ask X` alone
 * addresses X and asks about nothing (E10: `talk to X` is this); `ask about
 * Y` asks about Y with no one addressed; a bare `ask` is both absent and
 * earns the deflection.
 *
 * @param command - The parsed proposal
 * @returns The narrowed action — a question is never refused here
 */
export const narrowAskCommand = (command: ParsedCommand): CommandNarrowingOutcome => {
	const addressee = command.directObjectRef === undefined ? undefined : askPartyOf(command.directObjectRef);
	const subject =
		command.indirectObjectRef === undefined ? undefined : askSubjectOf(command.indirectObjectRef, command.indirectObjectSurface);

	return narrowed({
		...(addressee === undefined ? {} : { addressee }),
		...(subject === undefined ? {} : { subject }),
		type: 'ask',
	});
};

/**
 * A resolved reference as one party of a question: the room by its ref, an
 * actor by its ref (D232), a thing by its id.
 *
 * @param entityRef - The ref the parser resolved
 * @returns The party
 */
export const askPartyOf = (entityRef: EntityRef): AskParty => {
	const roomId = roomIdForEntityRef(entityRef);

	if (roomId !== undefined) {
		return { kind: 'room', roomId };
	}

	const actorId = actorIdForEntityRef(entityRef);

	return actorId === undefined ? { itemId: itemIdForEntityRef(entityRef), kind: 'item' } : { actorId, kind: 'actor' };
};

/**
 * A resolved `about` reference as a question's subject: a topic word by the
 * word, a party with the words the player used for it (D226, E20).
 *
 * @param entityRef - The ref the parser resolved for the `about` phrase
 * @param surface - What the player typed for its head, when the parser kept it
 * @returns The subject
 */
const askSubjectOf = (entityRef: EntityRef, surface: string | undefined): AskSubject => {
	const topicWord = topicWordForEntityRef(entityRef);

	if (topicWord !== undefined) {
		return { kind: 'word', word: topicWord };
	}

	return { ...askPartyOf(entityRef), ...(surface === undefined ? {} : { surface }) };
};
